import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users2, Search, Send, MapPin, Star, Sparkles, Wallet } from "lucide-react";
import CardPaymentDialog from "@/components/CardPaymentDialog";
import SentCallsSection from "@/pages/work/SentCallsSection";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FreelancersSection() {
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [invite, setInvite] = useState({ title: "", description: "", budget: 0, call_price: "", contact_email: "", contact_phone: "" });
  const [sending, setSending] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (city) params.set("city", city);
    const res = await axios.get(`${API}/freelancers?${params.toString()}`);
    setList(res.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const [cardPay, setCardPay] = useState(null);
  const buildPayload = () => ({ ...invite, budget: parseFloat(invite.budget) || 0, call_price: parseFloat(invite.call_price) || 0 });
  const sendInvite = async (afterCard = false) => {
    if (!invite.title || !invite.description) return toast.error("Título e descrição obrigatórios");
    setSending(true);
    try {
      await axios.post(`${API}/freelancers/${selected.user_id}/invite`, buildPayload());
      const price = parseFloat(invite.call_price) || 0;
      toast.success(`Convite enviado a ${selected.name}!${price > 0 ? ` R$ ${price.toFixed(2)} bloqueado em escrow.` : ""}`);
      setSelected(null);
      setInvite({ title: "", description: "", budget: 0, call_price: "", contact_email: "", contact_phone: "" });
    } catch (e) {
      if (e.response?.status === 402 && !afterCard) {
        toast.info("Saldo insuficiente — pague a chamada com cartão");
        setCardPay({ amount: parseFloat(invite.call_price) || 0 });
      } else toast.error(e.response?.data?.detail || "Erro");
    }
    setSending(false);
  };

  return (
    <div className="space-y-4">
      <SentCallsSection />
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="freelancers-search"
            placeholder="Buscar por skill, headline..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="pl-9"
          />
        </div>
        <Input
          data-testid="freelancers-city"
          placeholder="Cidade"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          className="max-w-[200px]"
        />
        <Button data-testid="freelancers-search-btn" onClick={load} className="rounded-full">Buscar</Button>
      </div>

      {list.length === 0 && (
        <Card className="p-10 text-center text-muted-foreground">
          <Users2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Nenhum freelancer encontrado. Cadastre-se em "Meu perfil" para aparecer aqui.
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {list.map(f => (
          <Card key={f.user_id} className="p-5" data-testid={`freelancer-${f.user_id}`}>
            <div className="flex items-start gap-3 mb-3">
              <Avatar className="w-14 h-14">
                <AvatarImage src={f.picture} />
                <AvatarFallback>{f.name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold truncate">{f.name}</h3>
                  {f.rating_avg && (
                    <span className="text-xs flex items-center gap-1 text-yellow-600">
                      <Star className="w-3 h-3 fill-current" /> {f.rating_avg.toFixed(1)}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground truncate">{f.headline}</div>
                {f.city && <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{f.city}</div>}
              </div>
              {f.availability && (
                <div className="text-right">
                  <Badge variant="secondary" className="text-[10px] uppercase">{f.availability === "full" ? "Integral" : f.availability === "part" ? "Parcial" : "Freelance"}</Badge>
                </div>
              )}
            </div>

            {(f.skills?.length > 0) && (
              <div className="flex flex-wrap gap-1 mb-3">
                {f.skills.slice(0, 6).map((s, i) => <Badge key={i} variant="secondary" className="text-[10px]">{s}</Badge>)}
                {f.skills.length > 6 && <span className="text-[10px] text-muted-foreground">+{f.skills.length - 6}</span>}
              </div>
            )}

            {f.bio && <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{f.bio}</p>}

            {(f.portfolio?.length > 0) && (
              <div className="mb-3" data-testid={`freelancer-portfolio-${f.user_id}`}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-semibold">Portfólio ({f.portfolio.length})</div>
                <div className="grid grid-cols-4 gap-1">
                  {f.portfolio.slice(0, 4).map((p, i) => (
                    <a
                      key={i}
                      href={p.url || "#"}
                      target={p.url ? "_blank" : undefined}
                      rel="noreferrer"
                      title={p.title}
                      className="aspect-square rounded overflow-hidden bg-muted flex items-center justify-center text-[9px] text-center p-1 hover:opacity-80"
                    >
                      {p.image_url ? <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" /> : <span className="text-muted-foreground truncate">{p.title}</span>}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <Button
              size="sm"
              className="w-full rounded-full gap-1"
              data-testid={`invite-btn-${f.user_id}`}
              onClick={() => setSelected(f)}
            >
              <Send className="w-4 h-4" /> Convidar para trabalho
            </Button>
          </Card>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Convidar {selected?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/30 text-xs flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              O freelancer receberá uma notificação. Se sua empresa for confidencial, o nome oculto será mantido.
            </div>
            <Input data-testid="invite-title" placeholder="Título da vaga" value={invite.title} onChange={(e) => setInvite({ ...invite, title: e.target.value })} />
            <Textarea data-testid="invite-desc" placeholder="Descrição / requisitos" rows={4} value={invite.description} onChange={(e) => setInvite({ ...invite, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="invite-budget" type="number" placeholder="Orçamento R$" value={invite.budget || ""} onChange={(e) => setInvite({ ...invite, budget: e.target.value })} />
              <Input data-testid="invite-email" placeholder="Seu e-mail (opcional)" value={invite.contact_email} onChange={(e) => setInvite({ ...invite, contact_email: e.target.value })} />
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/20 p-3 space-y-1">
              <label htmlFor="invite-call-price" className="text-xs font-semibold flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> Valor da chamada (R$) — pago agora, garantido em escrow</label>
              <Input id="invite-call-price" data-testid="invite-call-price" type="number" min="0" step="0.01" placeholder="0,00 (opcional)" value={invite.call_price || ""} onChange={(e) => setInvite({ ...invite, call_price: e.target.value })} />
              <p className="text-[11px] text-muted-foreground">Sai do saldo da carteira; se faltar, você paga com cartão salvo em 1 clique. É liberado ao freelancer quando você marcar a chamada como concluída.</p>
            </div>
            <Input data-testid="invite-phone" placeholder="Telefone (opcional)" value={invite.contact_phone} onChange={(e) => setInvite({ ...invite, contact_phone: e.target.value })} />
            <Button data-testid="invite-send" onClick={() => sendInvite(false)} disabled={sending} className="w-full rounded-full">
              {sending ? "Enviando..." : (parseFloat(invite.call_price) > 0 ? `Enviar e pagar R$ ${parseFloat(invite.call_price).toFixed(2)}` : "Enviar convite")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <CardPaymentDialog
        open={!!cardPay}
        onOpenChange={(v) => !v && setCardPay(null)}
        purpose="call_invite"
        amount={cardPay?.amount || 0}
        title="Pagar chamada com cartão"
        description="O valor é creditado na sua carteira e bloqueado em escrow até você concluir a chamada."
        onSuccess={() => { setCardPay(null); sendInvite(true); }}
      />
    </div>
  );
}
